import { FIRST_PAGE } from '@/data/hook/shared/usePagination';
import usePendencyAdvancedFilters, {
  PendencyAdvancedFilters,
} from '@/data/hook/shared/pendencies/usePendencyAdvancedFilters';
import { useDynamicParams } from '@/data/hook/useDynamicParams';
import {
  SCHEDULED_LESSON_ORDERING_FIELDS,
  ScheduledLessonFilters,
  ScheduledLessonOrdering,
} from '@/data/services/scheduledLessonsServices';
import { OrderingKey } from '@/models/Filter';
import { KlassStatusEnum } from '@/models/Klass';
import {
  DateRangeFilterParams,
  parseDateRangeParams,
} from '@/utils/filters/dateRangeParams';
import { createOrderingGuard } from '@/utils/filters/ordering';
import { getNumberParam } from '@/utils/filters/searchParams';

const DEFAULT_ORDERING: ScheduledLessonOrdering = '-datetime';

const isScheduledLessonOrdering = createOrderingGuard(
  SCHEDULED_LESSON_ORDERING_FIELDS,
);

export type PendenciesMainFilters = {
  search?: string;
  klassId?: number;
  courseBase?: number;
} & DateRangeFilterParams;

export type PendenciesAdvancedFilters = PendencyAdvancedFilters;

type PendenciesParams = {
  ordering: ScheduledLessonOrdering;
  page: number;
} & PendenciesMainFilters &
  PendenciesAdvancedFilters;

export default function usePendenciesFilter() {
  const { params, updateParams } = useDynamicParams<PendenciesParams>();

  const orderingParam = params.get('ordering');
  const ordering = isScheduledLessonOrdering(orderingParam)
    ? orderingParam
    : DEFAULT_ORDERING;

  const {
    advancedFilters,
    advancedFiltersCount,
    teacherFilter,
    unitsFilter,
    updateAdvancedFilters,
    clearAdvancedFilters,
  } = usePendencyAdvancedFilters();

  const mainFilters: PendenciesMainFilters = {
    search: params.get('search') || undefined,
    klassId: getNumberParam(params, 'klassId'),
    courseBase: getNumberParam(params, 'courseBase'),
    ...parseDateRangeParams(params),
  };

  const filters: ScheduledLessonFilters = {
    isPendingReport: true,
    teacher: teacherFilter,
    unit: unitsFilter,
    courseBase: mainFilters.courseBase ? [mainFilters.courseBase] : undefined,
    isActive: true,
    hasDone: false,
    ordering,
    klassStatus: [KlassStatusEnum.IN_PROGRESS, KlassStatusEnum.OPEN],
    search: mainFilters.search,
    klassId: mainFilters.klassId,
    dateRangeAfter: mainFilters.dateRangeAfter,
    dateRangeBefore: mainFilters.dateRangeBefore,
  };

  const toggleOrdering = (field: OrderingKey<ScheduledLessonOrdering>) =>
    updateParams({
      ordering: ordering === field ? `-${field}` : field,
      page: FIRST_PAGE,
    });

  const updateMainFilters = (updated: Partial<PendenciesMainFilters>) =>
    updateParams({ ...updated, page: FIRST_PAGE });

  return {
    filters,
    mainFilters,
    advancedFilters,
    advancedFiltersCount,
    toggleOrdering,
    updateMainFilters,
    updateAdvancedFilters,
    clearAdvancedFilters,
  };
}
